import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  BrainCircuit,
  CalendarDays,
  Check,
  ChevronRight,
  CircleHelp,
  CreditCard,
  FileText,
  LockKeyhole,
  MessageCircle,
  Palette,
  Save,
  ShieldCheck,
  SlidersHorizontal,
  Stethoscope,
  UsersRound
} from "lucide-react";

import { doc, updateDoc } from "../../access/firestore";
import { clinicSettingsPayload } from "../../access/settings";
import { db } from "../../firebase";
import { storage } from "../../firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useAccess } from "../../access/useAccess";
import { useClinic } from "../../context/ClinicContext";
import { useNotifications } from "../../notifications/NotificationProvider";

const DAYS = [
  ["lunes", "Lunes"],
  ["martes", "Martes"],
  ["miercoles", "Miércoles"],
  ["jueves", "Jueves"],
  ["viernes", "Viernes"],
  ["sabado", "Sábado"],
  ["domingo", "Domingo"]
];

const CLINICAL_MODULES = [
  ["history", "Historia clínica", "Expediente editable y completo"],
  ["treatment", "Planes de tratamiento", "Presupuestos y seguimiento"],
  ["evolution", "Notas de evolución", "Registro clínico por consulta"],
  ["photos", "Fotos clínicas", "Galería organizada por paciente"],
  ["imaging", "Imagenología", "Tomografías y estudios DICOM"],
  ["odontogram", "Odontograma", "Registro dental visual"],
  ["periodonto", "Periodontograma", "Mediciones periodontales"],
  ["prescriptions", "Recetas", "Prescripción y posología"],
  ["consents", "Consentimientos", "Documentos y firmas"],
  ["orthodontics", "Ortodoncia", "Cefalometría, Bolton y STL"]
];

const CONTROL_SECTIONS = [
  {
    id: "appearance",
    label: "Consultorio y marca",
    description: "Identidad, formato y experiencia visual",
    icon: Palette
  },
  {
    id: "agenda",
    label: "Agenda y citas",
    description: "Duraciones, horarios y reglas de agenda",
    icon: CalendarDays
  },
  {
    id: "patients",
    label: "Pacientes",
    description: "Campos, validaciones y organización",
    icon: UsersRound
  },
  {
    id: "clinical",
    label: "Módulos clínicos",
    description: "Activa y ordena las herramientas clínicas",
    icon: Stethoscope
  },
  {
    id: "finance",
    label: "Finanzas",
    description: "Moneda, pagos, recibos y presupuestos",
    icon: CreditCard
  },
  {
    id: "team",
    label: "Equipo y permisos",
    description: "Alcances y valores predeterminados",
    icon: UsersRound
  },
  {
    id: "ai",
    label: "VexaDent AI",
    description: "Preferencias y confirmaciones de IA",
    icon: BrainCircuit
  },
  {
    id: "security",
    label: "Seguridad y sistema",
    description: "Sesiones, actividad y respaldo",
    icon: ShieldCheck
  }
];

const DEFAULT_SETTINGS = {
  appearance: {
    density: "comfortable",
    dateFormat: "dd/MM/yyyy",
    timeFormat: "24h",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    accentColor: "#0e91ad",
    showBrandInDocuments: true,
    showContactInDocuments: true
  },
  agenda: {
    slotMinutes: 15,
    defaultDuration: 60,
    allowOverbooking: false,
    showWeekends: true,
    minimumNoticeHours: 2,
    cancellationWindowHours: 24,
    waitingList: true,
    showPatientPhone: true,
    workingDays: {},
    workingHours: {}
  },
  patients: {
    requirePhone: true,
    requireEmail: false,
    requireBirthDate: false,
    duplicateWarning: true,
    allowArchive: true,
    defaultStatus: "activo",
    patientCodePrefix: "PAC",
    customFields: ""
  },
  clinical: {
    enabledModules: Object.fromEntries(
      CLINICAL_MODULES.map(([key]) => [key, true])
    ),
    autoSaveDrafts: true,
    imageQuality: "balanced",
    toothNumbering: "FDI",
    requireEvolutionSignature: false,
    requireConsentBeforeTreatment: false
  },
  communication: {
    whatsappEnabled: false,
    reminder24h: true,
    reminder2h: false,
    autoConfirm: true,
    cancelledAppointment: true,
    appointmentCreated: true,
    paymentReceipt: true,
    birthdayMessages: false,
    sendFrom: "08:00",
    sendUntil: "20:00",
    signature: "",
    templates: {
      appointmentReminder: "Hola {{paciente}}, te recordamos tu cita mañana a las {{hora}} en {{consultorio}}.",
      appointmentConfirmation: "Tu cita en {{consultorio}} quedó confirmada para el {{fecha}} a las {{hora}}.",
      paymentReceipt: "Recibimos tu pago de {{monto}}. Gracias por tu confianza."
    }
  },
  finance: {
    currency: "MXN",
    allowPartialPayments: true,
    requirePaymentForAppointment: false,
    allowDiscounts: true,
    quoteValidityDays: 30,
    receiptPrefix: "REC",
    receiptFooter: "Gracias por tu confianza.",
    paymentMethods: {
      cash: true,
      card: true,
      transfer: true,
      other: false
    }
  },
  team: {
    defaultRole: "asistente",
    patientScope: "assigned",
    agendaScope: "own",
    requireInviteApproval: true,
    allowDoctorsToManageTemplates: false
  },
  ai: {
    enabled: true,
    language: "es-MX",
    tone: "professional",
    confirmBeforeActions: true,
    useClinicalContext: true,
    allowDrafts: true
  },
  integrations: {
    googleCalendarSync: false,
    whatsappVps: true,
    cloudImaging: false,
    automaticBackups: true
  },
  security: {
    sessionMinutes: 480,
    lockAfterInactivity: true,
    requirePasswordChange: false,
    activityLog: true,
    exportBeforeDelete: true
  }
};

function mergeSettings(value) {
  const source = value || {};
  return {
    ...DEFAULT_SETTINGS,
    ...source,
    appearance: { ...DEFAULT_SETTINGS.appearance, ...(source.appearance || {}) },
    agenda: { ...DEFAULT_SETTINGS.agenda, ...(source.agenda || {}) },
    patients: { ...DEFAULT_SETTINGS.patients, ...(source.patients || {}) },
    clinical: {
      ...DEFAULT_SETTINGS.clinical,
      ...(source.clinical || {}),
      enabledModules: {
        ...DEFAULT_SETTINGS.clinical.enabledModules,
        ...(source.clinical?.enabledModules || {})
      }
    },
    communication: {
      ...DEFAULT_SETTINGS.communication,
      ...(source.communication || {}),
      templates: {
        ...DEFAULT_SETTINGS.communication.templates,
        ...(source.communication?.templates || {})
      }
    },
    finance: {
      ...DEFAULT_SETTINGS.finance,
      ...(source.finance || {}),
      paymentMethods: {
        ...DEFAULT_SETTINGS.finance.paymentMethods,
        ...(source.finance?.paymentMethods || {})
      }
    },
    team: { ...DEFAULT_SETTINGS.team, ...(source.team || {}) },
    ai: { ...DEFAULT_SETTINGS.ai, ...(source.ai || {}) },
    integrations: { ...DEFAULT_SETTINGS.integrations, ...(source.integrations || {}) },
    security: { ...DEFAULT_SETTINGS.security, ...(source.security || {}) }
  };
}

function Toggle({ checked, onChange, label, description }) {
  return (
    <label className="vd-control-toggle-row">
      <span className="vd-control-toggle-copy">
        <strong>{label}</strong>
        {description && <small>{description}</small>}
      </span>
      <input
        type="checkbox"
        checked={Boolean(checked)}
        onChange={event => onChange(event.target.checked)}
      />
      <span className="vd-control-toggle" aria-hidden="true"><span /></span>
    </label>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="vd-control-field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function SectionCard({ title, description, icon: Icon, children }) {
  return (
    <section className="vd-control-card">
      <div className="vd-control-card-heading">
        <div className="vd-control-card-icon"><Icon size={18} /></div>
        <div>
          <h3>{title}</h3>
          {description && <p>{description}</p>}
        </div>
      </div>
      <div className="vd-control-card-body">{children}</div>
    </section>
  );
}

export default function AdvancedClinicSettings() {
  const { clinicId, clinicData } = useClinic();
  const { can } = useAccess();
  const { notify } = useNotifications();
  const [section, setSection] = useState("appearance");
  const [query, setQuery] = useState("");
  const [settings, setSettings] = useState(() => mergeSettings());
  const [profile, setProfile] = useState({
    nombreClinica: "",
    telefono: "",
    email: "",
    RFC: "",
    logoURL: "",
    direccion: {
      calle: "",
      numeroExterior: "",
      numeroInterior: "",
      codigoPostal: "",
      colonia: "",
      ciudad: "",
      estado: "",
      pais: "México"
    }
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (clinicData) {
      const next = mergeSettings(clinicData.settings);
      const savedDays = clinicData.settings?.agenda?.workingDays;
      if (!savedDays || Object.keys(savedDays).length === 0) {
        next.agenda.workingDays = Object.fromEntries(
          DAYS.map(([key]) => [key, clinicData.horariosLaborales?.[key]?.activo !== false])
        );
      }
      if (!clinicData.settings?.agenda?.workingHours || Object.keys(clinicData.settings.agenda.workingHours).length === 0) {
        next.agenda.workingHours = Object.fromEntries(
          DAYS.map(([key]) => [key, { ...(clinicData.horariosLaborales?.[key] || {}) }])
        );
      }
      if (clinicData.settings?.communication?.whatsappEnabled == null) {
        next.communication.whatsappEnabled = clinicData.whatsappEnabled === true;
      }
      setSettings(next);
      setProfile({
        nombreClinica: clinicData.nombreClinica || "",
        telefono: clinicData.telefono || "",
        email: clinicData.email || "",
        RFC: clinicData.RFC || "",
        logoURL: clinicData.logoURL || "",
        direccion: {
          pais: "México",
          ...(clinicData.direccion || {})
        }
      });
      setDirty(false);
    }
  }, [clinicData]);

  const visibleSections = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return CONTROL_SECTIONS;
    return CONTROL_SECTIONS.filter(item =>
      `${item.label} ${item.description}`.toLowerCase().includes(term)
    );
  }, [query]);

  const active = CONTROL_SECTIONS.find(item => item.id === section) || CONTROL_SECTIONS[0];

  const updateSection = (sectionId, key, value) => {
    setSettings(previous => ({
      ...previous,
      [sectionId]: { ...previous[sectionId], [key]: value }
    }));
    setDirty(true);
  };

  const updateNested = (sectionId, parentKey, key, value) => {
    setSettings(previous => ({
      ...previous,
      [sectionId]: {
        ...previous[sectionId],
        [parentKey]: { ...previous[sectionId][parentKey], [key]: value }
      }
    }));
    setDirty(true);
  };

  const updateProfile = (key, value) => {
    setProfile(previous => ({ ...previous, [key]: value }));
    setDirty(true);
  };

  const updateAddress = (key, value) => {
    setProfile(previous => ({
      ...previous,
      direccion: { ...previous.direccion, [key]: value }
    }));
    setDirty(true);
  };

  const updateWorkingHour = (day, key, value) => {
    setSettings(previous => ({
      ...previous,
      agenda: {
        ...previous.agenda,
        workingHours: {
          ...previous.agenda.workingHours,
          [day]: {
            ...(previous.agenda.workingHours?.[day] || {}),
            [key]: value
          }
        }
      }
    }));
    setDirty(true);
  };

  const uploadLogo = async event => {
    const file = event.target.files?.[0];
    if (!file || !clinicId) return;
    try {
      const logoRef = ref(storage, `logos/${clinicId}`);
      await uploadBytes(logoRef, file);
      const logoURL = await getDownloadURL(logoRef);
      updateProfile("logoURL", logoURL);
      notify({
        source: "vexa-alert",
        tone: "success",
        title: "Logo listo",
        message: "Guarda la configuración para aplicarlo en todo el consultorio."
      });
    } catch (error) {
      notify({
        source: "vexa-alert",
        tone: "error",
        title: "No se pudo subir el logo",
        message: error.message || "Intenta nuevamente."
      });
    }
  };

  const save = async () => {
    if (!clinicId || !can("settings", "clinic")) return;
    try {
      setSaving(true);
      const currentNotifications = clinicData?.notifications || {};
      const currentSchedule = clinicData?.horariosLaborales || {};
      const horariosLaborales = Object.fromEntries(
        DAYS.map(([key]) => [
          key,
          {
            ...(currentSchedule[key] || {}),
            ...(settings.agenda.workingHours?.[key] || {}),
            activo: settings.agenda.workingDays[key] !== false
          }
        ])
      );
      const payload = clinicSettingsPayload({
        nombreClinica: profile.nombreClinica,
        telefono: profile.telefono,
        email: profile.email,
        RFC: profile.RFC,
        direccion: profile.direccion,
        logoURL: profile.logoURL,
        settings,
        intervaloAgenda: String(settings.agenda.slotMinutes),
        defaultAppointmentDuration: String(settings.agenda.defaultDuration),
        timeZone: settings.appearance.timeZone,
        horariosLaborales,
        notifications: {
          ...currentNotifications,
          reminder24h: settings.communication.reminder24h,
          reminder2h: settings.communication.reminder2h,
          autoConfirm: settings.communication.autoConfirm,
          cancelledAppointment: settings.communication.cancelledAppointment,
          whatsappMessages: settings.communication.whatsappEnabled
        },
        whatsappEnabled: settings.communication.whatsappEnabled
      }, can);
      await updateDoc(doc(db, "clinics", clinicId), payload);
      setDirty(false);
      notify({
        source: "vexa-alert",
        tone: "success",
        title: "Configuración actualizada",
        message: "Las preferencias del consultorio se guardaron correctamente."
      });
    } catch (error) {
      notify({
        source: "vexa-alert",
        tone: "error",
        title: "No se pudo guardar",
        message: error.message || "Intenta nuevamente."
      });
    } finally {
      setSaving(false);
    }
  };

  const renderAppearance = () => (
    <>
      <SectionCard title="Datos del consultorio" description="Esta información aparece en la aplicación y en los documentos que entregas a tus pacientes." icon={Stethoscope}>
        <div className="vd-control-profile-row">
          <div className="vd-control-logo-preview">{profile.logoURL ? <img src={profile.logoURL} alt="Logo del consultorio" /> : <span>V</span>}</div>
          <div className="vd-control-profile-copy"><strong>{profile.nombreClinica || "Tu consultorio"}</strong><small>Logo del consultorio</small></div>
          <label className="vd-control-upload">Cambiar logo<input type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadLogo} /></label>
        </div>
        <div className="vd-control-grid vd-control-grid-2">
          <Field label="Nombre del consultorio"><input value={profile.nombreClinica} onChange={event => updateProfile("nombreClinica", event.target.value)} placeholder="Ej. VexaDent Centro" /></Field>
          <Field label="Teléfono"><input value={profile.telefono} onChange={event => updateProfile("telefono", event.target.value)} placeholder="(000) 000 0000" /></Field>
          <Field label="Correo de contacto"><input type="email" value={profile.email} onChange={event => updateProfile("email", event.target.value)} placeholder="contacto@consultorio.com" /></Field>
          <Field label="RFC (opcional)"><input value={profile.RFC} onChange={event => updateProfile("RFC", event.target.value.toUpperCase())} placeholder="Aún no disponible" /></Field>
        </div>
        <div className="vd-control-divider" />
        <div className="vd-control-grid vd-control-grid-3">
          <Field label="Calle"><input value={profile.direccion.calle || ""} onChange={event => updateAddress("calle", event.target.value)} /></Field>
          <Field label="Número exterior"><input value={profile.direccion.numeroExterior || ""} onChange={event => updateAddress("numeroExterior", event.target.value)} /></Field>
          <Field label="Número interior"><input value={profile.direccion.numeroInterior || ""} onChange={event => updateAddress("numeroInterior", event.target.value)} /></Field>
          <Field label="Código postal"><input value={profile.direccion.codigoPostal || ""} onChange={event => updateAddress("codigoPostal", event.target.value)} /></Field>
          <Field label="Colonia"><input value={profile.direccion.colonia || ""} onChange={event => updateAddress("colonia", event.target.value)} /></Field>
          <Field label="Ciudad"><input value={profile.direccion.ciudad || ""} onChange={event => updateAddress("ciudad", event.target.value)} /></Field>
          <Field label="Estado"><input value={profile.direccion.estado || ""} onChange={event => updateAddress("estado", event.target.value)} /></Field>
        </div>
      </SectionCard>
      <SectionCard title="Identidad y experiencia" description="Define cómo se presenta VexaDent para tu equipo y tus pacientes." icon={Palette}>
        <div className="vd-control-grid vd-control-grid-2">
          <Field label="Densidad de la interfaz">
            <select value={settings.appearance.density} onChange={event => updateSection("appearance", "density", event.target.value)}>
              <option value="comfortable">Cómoda</option>
              <option value="compact">Compacta</option>
            </select>
          </Field>
          <Field label="Zona horaria">
            <select value={settings.appearance.timeZone} onChange={event => updateSection("appearance", "timeZone", event.target.value)}>
              <option value="America/Mexico_City">Ciudad de México</option>
              <option value="America/Monterrey">Monterrey</option>
              <option value="America/Cancun">Cancún</option>
              <option value="America/Chihuahua">Chihuahua</option>
              <option value="America/Ciudad_Juarez">Ciudad Juárez</option>
            </select>
          </Field>
          <Field label="Formato de fecha">
            <select value={settings.appearance.dateFormat} onChange={event => updateSection("appearance", "dateFormat", event.target.value)}>
              <option value="dd/MM/yyyy">31/12/2026</option>
              <option value="MM/dd/yyyy">12/31/2026</option>
              <option value="yyyy-MM-dd">2026-12-31</option>
            </select>
          </Field>
          <Field label="Formato de hora">
            <select value={settings.appearance.timeFormat} onChange={event => updateSection("appearance", "timeFormat", event.target.value)}>
              <option value="24h">24 horas</option>
              <option value="12h">12 horas (AM/PM)</option>
            </select>
          </Field>
        </div>
        <div className="vd-control-divider" />
        <Toggle checked={settings.appearance.showBrandInDocuments} onChange={value => updateSection("appearance", "showBrandInDocuments", value)} label="Mostrar logo en documentos" description="Usa el logo actual del consultorio en recetas, presupuestos y recibos." />
        <Toggle checked={settings.appearance.showContactInDocuments} onChange={value => updateSection("appearance", "showContactInDocuments", value)} label="Mostrar datos de contacto" description="Incluye teléfono, correo y dirección en documentos generados." />
      </SectionCard>
    </>
  );

  const renderAgenda = () => (
    <>
      <SectionCard title="Reglas de la agenda" description="Haz que la agenda se comporte como trabaja tu consultorio." icon={CalendarDays}>
        <div className="vd-control-grid vd-control-grid-3">
          <Field label="Intervalo de agenda"><select value={settings.agenda.slotMinutes} onChange={event => updateSection("agenda", "slotMinutes", Number(event.target.value))}><option value={5}>5 minutos</option><option value={10}>10 minutos</option><option value={15}>15 minutos</option><option value={20}>20 minutos</option><option value={30}>30 minutos</option></select></Field>
          <Field label="Duración predeterminada"><select value={settings.agenda.defaultDuration} onChange={event => updateSection("agenda", "defaultDuration", Number(event.target.value))}><option value={30}>30 minutos</option><option value={45}>45 minutos</option><option value={60}>60 minutos</option><option value={90}>90 minutos</option><option value={120}>120 minutos</option></select></Field>
          <Field label="Anticipación mínima"><select value={settings.agenda.minimumNoticeHours} onChange={event => updateSection("agenda", "minimumNoticeHours", Number(event.target.value))}><option value={0}>Sin límite</option><option value={2}>2 horas</option><option value={6}>6 horas</option><option value={24}>24 horas</option></select></Field>
          <Field label="Ventana de cancelación"><select value={settings.agenda.cancellationWindowHours} onChange={event => updateSection("agenda", "cancellationWindowHours", Number(event.target.value))}><option value={0}>Sin límite</option><option value={12}>12 horas</option><option value={24}>24 horas</option><option value={48}>48 horas</option></select></Field>
        </div>
        <div className="vd-control-divider" />
        <Toggle checked={settings.agenda.allowOverbooking} onChange={value => updateSection("agenda", "allowOverbooking", value)} label="Permitir sobrecupo" description="Permite registrar una cita aunque el horario ya tenga ocupación." />
        <Toggle checked={settings.agenda.showWeekends} onChange={value => updateSection("agenda", "showWeekends", value)} label="Mostrar fines de semana" description="Mantiene sábado y domingo visibles en la agenda." />
        <Toggle checked={settings.agenda.waitingList} onChange={value => updateSection("agenda", "waitingList", value)} label="Usar lista de espera" description="Agrega pacientes cuando no haya disponibilidad inmediata." />
      </SectionCard>
      <SectionCard title="Horario de atención" description="Define qué días se muestran como disponibles para tu consultorio." icon={CalendarDays}>
        <div className="vd-control-days-grid">{DAYS.map(([key, label]) => <label key={key}><input type="checkbox" checked={settings.agenda.workingDays[key] !== false} onChange={event => updateNested("agenda", "workingDays", key, event.target.checked)} /><span>{label}</span></label>)}</div>
        <div className="vd-control-hours-grid">
          <div className="vd-control-hours-head"><span>Día</span><span>Apertura</span><span>Comida inicia</span><span>Comida termina</span><span>Cierre</span></div>
          {DAYS.map(([key, label]) => {
            const day = settings.agenda.workingHours?.[key] || {};
            const enabled = settings.agenda.workingDays[key] !== false;
            return <div className={`vd-control-hours-row${enabled ? "" : " is-disabled"}`} key={key}><strong>{label}</strong><input type="time" disabled={!enabled} value={day.apertura || ""} onChange={event => updateWorkingHour(key, "apertura", event.target.value)} /><input type="time" disabled={!enabled} value={day.comidaInicio || ""} onChange={event => updateWorkingHour(key, "comidaInicio", event.target.value)} /><input type="time" disabled={!enabled} value={day.comidaFin || ""} onChange={event => updateWorkingHour(key, "comidaFin", event.target.value)} /><input type="time" disabled={!enabled} value={day.cierre || ""} onChange={event => updateWorkingHour(key, "cierre", event.target.value)} /></div>;
          })}
        </div>
      </SectionCard>
    </>
  );

  const renderPatients = () => (
    <>
      <SectionCard title="Alta y expediente" description="Decide qué información necesita tu equipo para crear un paciente." icon={UsersRound}>
        <Toggle checked={settings.patients.requirePhone} onChange={value => updateSection("patients", "requirePhone", value)} label="Solicitar teléfono" description="Ayuda a mantener lista la comunicación automática." />
        <Toggle checked={settings.patients.requireEmail} onChange={value => updateSection("patients", "requireEmail", value)} label="Solicitar correo electrónico" />
        <Toggle checked={settings.patients.requireBirthDate} onChange={value => updateSection("patients", "requireBirthDate", value)} label="Solicitar fecha de nacimiento" />
        <Toggle checked={settings.patients.duplicateWarning} onChange={value => updateSection("patients", "duplicateWarning", value)} label="Advertir posibles duplicados" description="Busca coincidencias por nombre, teléfono y correo antes de crear." />
        <Toggle checked={settings.patients.allowArchive} onChange={value => updateSection("patients", "allowArchive", value)} label="Permitir archivar pacientes" />
        <div className="vd-control-grid vd-control-grid-2 vd-control-grid-spaced">
          <Field label="Prefijo de expediente"><input value={settings.patients.patientCodePrefix} maxLength={8} onChange={event => updateSection("patients", "patientCodePrefix", event.target.value.toUpperCase())} /></Field>
          <Field label="Estado inicial"><select value={settings.patients.defaultStatus} onChange={event => updateSection("patients", "defaultStatus", event.target.value)}><option value="activo">Activo</option><option value="prospecto">Prospecto</option><option value="inactivo">Inactivo</option></select></Field>
          <Field label="Campos personalizados" hint="Sepáralos con comas; ejemplo: Ocupación, Referido por, Seguro"><textarea rows={3} value={settings.patients.customFields} onChange={event => updateSection("patients", "customFields", event.target.value)} /></Field>
        </div>
      </SectionCard>
    </>
  );

  const renderClinical = () => (
    <>
      <SectionCard title="Herramientas clínicas" description="Activa solo lo que tu equipo utiliza y mantén la experiencia limpia." icon={Stethoscope}>
        <div className="vd-control-module-grid">{CLINICAL_MODULES.map(([key, label, description]) => <Toggle key={key} checked={settings.clinical.enabledModules[key]} onChange={value => updateNested("clinical", "enabledModules", key, value)} label={label} description={description} />)}</div>
        <div className="vd-control-divider" />
        <Toggle checked={settings.clinical.autoSaveDrafts} onChange={value => updateSection("clinical", "autoSaveDrafts", value)} label="Guardar borradores automáticamente" description="Evita perder avances al cambiar de paciente o módulo." />
        <Toggle checked={settings.clinical.requireEvolutionSignature} onChange={value => updateSection("clinical", "requireEvolutionSignature", value)} label="Solicitar firma en notas de evolución" />
        <Toggle checked={settings.clinical.requireConsentBeforeTreatment} onChange={value => updateSection("clinical", "requireConsentBeforeTreatment", value)} label="Solicitar consentimiento antes del tratamiento" />
      </SectionCard>
      <SectionCard title="Formato clínico" description="Personaliza convenciones que usa el equipo al registrar información." icon={FileText}>
        <div className="vd-control-grid vd-control-grid-2"><Field label="Numeración dental"><select value={settings.clinical.toothNumbering} onChange={event => updateSection("clinical", "toothNumbering", event.target.value)}><option value="FDI">FDI</option><option value="Universal">Universal</option><option value="Palmer">Palmer</option></select></Field><Field label="Calidad de imágenes"><select value={settings.clinical.imageQuality} onChange={event => updateSection("clinical", "imageQuality", event.target.value)}><option value="light">Ligera</option><option value="balanced">Balanceada</option><option value="high">Alta</option></select></Field></div>
      </SectionCard>
    </>
  );

  const renderCommunication = () => (
    <>
      <SectionCard title="Automatizaciones de WhatsApp" description="VexaDent puede hacer el seguimiento rutinario sin que el doctor escriba cada mensaje." icon={MessageCircle}>
        <Toggle checked={settings.communication.whatsappEnabled} onChange={value => updateSection("communication", "whatsappEnabled", value)} label="Activar comunicación por WhatsApp" description="Usa la conexión actual de WhatsApp del consultorio." />
        <div className="vd-control-grid vd-control-grid-2 vd-control-grid-spaced"><Field label="Horario de envío desde"><input type="time" value={settings.communication.sendFrom} onChange={event => updateSection("communication", "sendFrom", event.target.value)} /></Field><Field label="Horario de envío hasta"><input type="time" value={settings.communication.sendUntil} onChange={event => updateSection("communication", "sendUntil", event.target.value)} /></Field></div>
        <div className="vd-control-divider" />
        <div className="vd-control-automation-grid">
          <Toggle checked={settings.communication.reminder24h} onChange={value => updateSection("communication", "reminder24h", value)} label="Recordatorio automático 24 h antes" description="Se envía sin intervención manual." />
          <Toggle checked={settings.communication.reminder2h} onChange={value => updateSection("communication", "reminder2h", value)} label="Recordatorio automático 2 h antes" description="Segundo aviso opcional." />
          <Toggle checked={settings.communication.autoConfirm} onChange={value => updateSection("communication", "autoConfirm", value)} label="Confirmación al crear cita" />
          <Toggle checked={settings.communication.cancelledAppointment} onChange={value => updateSection("communication", "cancelledAppointment", value)} label="Aviso de cancelación" />
          <Toggle checked={settings.communication.appointmentCreated} onChange={value => updateSection("communication", "appointmentCreated", value)} label="Aviso de cita creada" />
          <Toggle checked={settings.communication.paymentReceipt} onChange={value => updateSection("communication", "paymentReceipt", value)} label="Recibo automático de pago" />
          <Toggle checked={settings.communication.birthdayMessages} onChange={value => updateSection("communication", "birthdayMessages", value)} label="Mensajes de cumpleaños" />
        </div>
      </SectionCard>
      <SectionCard title="Plantillas y firma" description="Personaliza el tono sin perder las variables automáticas de VexaDent." icon={FileText}>
        <Field label="Firma de mensajes" hint="Aparece al final de los mensajes automáticos"><input value={settings.communication.signature} onChange={event => updateSection("communication", "signature", event.target.value)} placeholder="Equipo de VexaDent" /></Field>
        <Field label="Recordatorio de cita"><textarea rows={3} value={settings.communication.templates.appointmentReminder} onChange={event => updateNested("communication", "templates", "appointmentReminder", event.target.value)} /></Field>
        <Field label="Confirmación de cita"><textarea rows={3} value={settings.communication.templates.appointmentConfirmation} onChange={event => updateNested("communication", "templates", "appointmentConfirmation", event.target.value)} /></Field>
        <Field label="Recibo de pago"><textarea rows={3} value={settings.communication.templates.paymentReceipt} onChange={event => updateNested("communication", "templates", "paymentReceipt", event.target.value)} /></Field>
      </SectionCard>
    </>
  );

  const renderFinance = () => (
    <>
      <SectionCard title="Operación financiera" description="Configura cómo se registran presupuestos, pagos y recibos." icon={CreditCard}>
        <div className="vd-control-grid vd-control-grid-3"><Field label="Moneda"><select value={settings.finance.currency} onChange={event => updateSection("finance", "currency", event.target.value)}><option value="MXN">MXN · Peso mexicano</option><option value="USD">USD · Dólar estadounidense</option><option value="EUR">EUR · Euro</option></select></Field><Field label="Vigencia de presupuesto"><select value={settings.finance.quoteValidityDays} onChange={event => updateSection("finance", "quoteValidityDays", Number(event.target.value))}><option value={0}>Sin vencimiento</option><option value={15}>15 días</option><option value={30}>30 días</option><option value={60}>60 días</option></select></Field><Field label="Prefijo de recibo"><input value={settings.finance.receiptPrefix} maxLength={8} onChange={event => updateSection("finance", "receiptPrefix", event.target.value.toUpperCase())} /></Field></div>
        <div className="vd-control-divider" />
        <Toggle checked={settings.finance.allowPartialPayments} onChange={value => updateSection("finance", "allowPartialPayments", value)} label="Permitir pagos parciales" />
        <Toggle checked={settings.finance.requirePaymentForAppointment} onChange={value => updateSection("finance", "requirePaymentForAppointment", value)} label="Solicitar pago antes de confirmar cita" />
        <Toggle checked={settings.finance.allowDiscounts} onChange={value => updateSection("finance", "allowDiscounts", value)} label="Permitir descuentos" />
        <div className="vd-control-payment-methods"><strong>Métodos de pago visibles</strong><div>{Object.entries({ cash: "Efectivo", card: "Tarjeta", transfer: "Transferencia", other: "Otro" }).map(([key, label]) => <label key={key}><input type="checkbox" checked={settings.finance.paymentMethods[key]} onChange={event => updateNested("finance", "paymentMethods", key, event.target.checked)} /><span>{label}</span></label>)}</div></div>
      </SectionCard>
      <SectionCard title="Recibos" description="Añade un texto consistente a tus comprobantes." icon={FileText}><Field label="Pie de recibo"><textarea rows={3} value={settings.finance.receiptFooter} onChange={event => updateSection("finance", "receiptFooter", event.target.value)} /></Field></SectionCard>
    </>
  );

  const renderTeam = () => (
    <SectionCard title="Reglas para el equipo" description="Define valores predeterminados; los permisos detallados siguen gestionándose en Usuarios." icon={UsersRound}>
      <div className="vd-control-grid vd-control-grid-2"><Field label="Rol predeterminado al invitar"><select value={settings.team.defaultRole} onChange={event => updateSection("team", "defaultRole", event.target.value)}><option value="asistente">Asistente</option><option value="recepcionista">Recepción</option><option value="doctor">Doctor</option></select></Field><Field label="Alcance de pacientes"><select value={settings.team.patientScope} onChange={event => updateSection("team", "patientScope", event.target.value)}><option value="assigned">Solo asignados</option><option value="all">Todos los pacientes</option></select></Field><Field label="Alcance de agenda"><select value={settings.team.agendaScope} onChange={event => updateSection("team", "agendaScope", event.target.value)}><option value="own">Agenda propia</option><option value="team">Agenda del equipo</option></select></Field></div>
      <div className="vd-control-divider" />
      <Toggle checked={settings.team.requireInviteApproval} onChange={value => updateSection("team", "requireInviteApproval", value)} label="Aprobar nuevas invitaciones" description="Evita que una invitación se active sin revisión." />
      <Toggle checked={settings.team.allowDoctorsToManageTemplates} onChange={value => updateSection("team", "allowDoctorsToManageTemplates", value)} label="Permitir que doctores gestionen plantillas" />
      <div className="vd-control-info"><CircleHelp size={16} /><span>La administración granular por módulo, acción y alcance se mantiene en el módulo Usuarios para no duplicar permisos.</span></div>
    </SectionCard>
  );

  const renderAI = () => (
    <SectionCard title="VexaDent AI" description="Controla el tono, el contexto y cuándo la IA debe pedir confirmación." icon={BrainCircuit}>
      <Toggle checked={settings.ai.enabled} onChange={value => updateSection("ai", "enabled", value)} label="Activar VexaDent AI" description="Permite usar las herramientas de inteligencia artificial del consultorio." />
      <div className="vd-control-grid vd-control-grid-2 vd-control-grid-spaced"><Field label="Idioma"><select value={settings.ai.language} onChange={event => updateSection("ai", "language", event.target.value)}><option value="es-MX">Español (México)</option><option value="es-ES">Español (España)</option><option value="en-US">English (US)</option></select></Field><Field label="Tono"><select value={settings.ai.tone} onChange={event => updateSection("ai", "tone", event.target.value)}><option value="professional">Profesional</option><option value="friendly">Cercano</option><option value="concise">Breve y directo</option></select></Field></div>
      <div className="vd-control-divider" />
      <Toggle checked={settings.ai.confirmBeforeActions} onChange={value => updateSection("ai", "confirmBeforeActions", value)} label="Pedir confirmación antes de acciones" description="La IA preparará el resultado, pero no ejecutará cambios sin aprobación." />
      <Toggle checked={settings.ai.useClinicalContext} onChange={value => updateSection("ai", "useClinicalContext", value)} label="Usar el contexto clínico autorizado" description="Permite que las respuestas aprovechen la información disponible para ese paciente." />
      <Toggle checked={settings.ai.allowDrafts} onChange={value => updateSection("ai", "allowDrafts", value)} label="Permitir borradores asistidos" />
    </SectionCard>
  );

  const renderIntegrations = () => (
    <SectionCard title="Servicios conectados" description="Consulta qué integraciones puede utilizar tu consultorio y decide su comportamiento." icon={SlidersHorizontal}>
      <Toggle checked={settings.integrations.googleCalendarSync} onChange={value => updateSection("integrations", "googleCalendarSync", value)} label="Sincronizar Google Calendar" description="La conexión y permisos se completan en Integraciones." />
      <Toggle checked={settings.integrations.whatsappVps} onChange={value => updateSection("integrations", "whatsappVps", value)} label="Usar automatizaciones de WhatsApp" />
      <Toggle checked={settings.integrations.cloudImaging} onChange={value => updateSection("integrations", "cloudImaging", value)} label="Guardar copias de imagenología en nube" description="Déjalo apagado si solo quieres trabajar con archivos locales." />
      <Toggle checked={settings.integrations.automaticBackups} onChange={value => updateSection("integrations", "automaticBackups", value)} label="Respaldos automáticos" />
      <div className="vd-control-info"><CircleHelp size={16} /><span>Las claves secretas, Stripe y conexiones externas se siguen administrando en sus pantallas seguras; aquí solo se guarda la preferencia de uso.</span></div>
    </SectionCard>
  );

  const renderSecurity = () => (
    <SectionCard title="Seguridad y continuidad" description="Ajusta el comportamiento de las sesiones y la trazabilidad del consultorio." icon={LockKeyhole}>
      <div className="vd-control-grid vd-control-grid-2"><Field label="Cerrar sesión por inactividad"><select value={settings.security.sessionMinutes} onChange={event => updateSection("security", "sessionMinutes", Number(event.target.value))}><option value={60}>1 hora</option><option value={240}>4 horas</option><option value={480}>8 horas</option><option value={0}>No cerrar automáticamente</option></select></Field></div>
      <div className="vd-control-divider" />
      <Toggle checked={settings.security.lockAfterInactivity} onChange={value => updateSection("security", "lockAfterInactivity", value)} label="Bloquear sesión por inactividad" description="Protege la cuenta cuando el consultorio queda abierto." />
      <Toggle checked={settings.security.requirePasswordChange} onChange={value => updateSection("security", "requirePasswordChange", value)} label="Solicitar cambio de contraseña" />
      <Toggle checked={settings.security.activityLog} onChange={value => updateSection("security", "activityLog", value)} label="Registrar actividad del consultorio" />
      <Toggle checked={settings.security.exportBeforeDelete} onChange={value => updateSection("security", "exportBeforeDelete", value)} label="Solicitar respaldo antes de eliminar" />
    </SectionCard>
  );

  const renderSection = () => {
    switch (section) {
      case "appearance": return renderAppearance();
      case "agenda": return renderAgenda();
      case "patients": return renderPatients();
      case "clinical": return renderClinical();
      case "communication": return renderCommunication();
      case "finance": return renderFinance();
      case "team": return renderTeam();
      case "ai": return renderAI();
      case "integrations": return renderIntegrations();
      case "security": return renderSecurity();
      default: return renderAppearance();
    }
  };

  return (
    <div className="vd-control-center">
      <aside className="vd-control-sidebar">
        <div className="vd-control-sidebar-title"><SlidersHorizontal size={17} /><div><strong>Configuración completa</strong><small>Todos los módulos de tu consultorio</small></div></div>
        <label className="vd-control-search"><Activity size={15} /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Buscar configuración" /></label>
        <nav className="vd-control-nav">
          {visibleSections.map(item => { const Icon = item.icon; return <button key={item.id} type="button" className={section === item.id ? "is-active" : ""} onClick={() => setSection(item.id)}><span><Icon size={16} /></span><div><strong>{item.label}</strong><small>{item.description}</small></div><ChevronRight size={15} /></button>; })}
        </nav>
        <div className="vd-control-sidebar-note"><ShieldCheck size={15} /><span>Los cambios aplican al consultorio completo.</span></div>
      </aside>
      <div className="vd-control-content">
        <div className="vd-control-content-head"><div><p>Configuración del consultorio</p><h2>{active.label}</h2><span>{active.description}</span></div><div className={`vd-control-save-state${dirty ? " is-dirty" : ""}`}>{dirty ? <><span /> Cambios sin guardar</> : <><Check size={14} /> Todo guardado</>}</div></div>
        {renderSection()}
        <div className="vd-control-save-bar"><span>{dirty ? "Tienes cambios pendientes en esta configuración." : "Tu configuración está al día."}</span><button type="button" onClick={save} disabled={!dirty || saving || !can("settings", "clinic")}><Save size={16} />{saving ? "Guardando..." : "Guardar configuración"}</button></div>
      </div>
    </div>
  );
}
